import React, { useState, useEffect } from "react";
import "./projects.css";
import {
  addProject,
  addModuleByEmployee,
  getAllMyProjects
} from "../../service/project_service";
import { toast } from "react-toastify";

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Project Form State
  const [projectForm, setProjectForm] = useState({
    projectname: "",
    description: "",
    startdate: "",
    enddate: "",
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);

  // Module Form State
  const [moduleForm, setModuleForm] = useState({
    projectid: "",
    modulename: "",
    description: "",
  });
  const [isSubmittingModule, setIsSubmittingModule] = useState(false);

  useEffect(() => {
    fetchMyProjects();
  }, []);

  const fetchMyProjects = async () => {
    try {
      const response = await getAllMyProjects();
      if (response.status === "success" && response.data) {
        setProjects(response.data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!projectForm.projectname.trim()) return toast.error("Project name is required");
    if (!projectForm.description.trim()) return toast.error("Description is required");
    if (!projectForm.startdate) return toast.error("Start date is required");
    if (!projectForm.enddate) return toast.error("End date is required");

    if (new Date(projectForm.startdate) > new Date(projectForm.enddate)) {
      return toast.error("End date cannot be before start date");
    }

    setIsSubmittingProject(true);
    try {
      const response = await addProject(projectForm);
      if (response.status === "success") {
        toast.success("Project created successfully!");
        setProjectForm({
          projectname: "",
          description: "",
          startdate: "",
          enddate: "",
        });
        fetchMyProjects(); // Refresh project list
      } else {
        toast.error(response.message || "Failed to create project");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const handleModuleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!moduleForm.projectid) return toast.error("Please select a project");
    if (!moduleForm.modulename.trim()) return toast.error("Module name is required");
    if (!moduleForm.description.trim()) return toast.error("Description is required");

    setIsSubmittingModule(true);
    try {
      const response = await addModuleByEmployee(moduleForm);
      if (response.status === "success") {
        toast.success("Module created and assigned successfully!");
        setModuleForm({
          projectid: "",
          modulename: "",
          description: "",
        });
      } else {
        toast.error(response.message || "Failed to create module");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setIsSubmittingModule(false);
    }
  };

  return (
    <div className="projects-view-container">
      <div className="projects-header">
        <h4>Projects Management</h4>
        <p>Create and manage projects and their modules</p>
      </div>

      <div className="projects-grid-layout">
        {/* Create Project Form */}
        <div className="project-form-card">
          <div className="form-header">
            <h5>Create Project</h5>
          </div>
          <form className="crm-form" onSubmit={handleProjectSubmit}>
            <div className="form-group-row">
              <div className="form-field">
                <label>Project Name</label>
                <input
                  type="text"
                  placeholder="Enter project name"
                  value={projectForm.projectname}
                  onChange={(e) => setProjectForm({ ...projectForm, projectname: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-group-row">
              <div className="form-field">
                <label>Description</label>
                <textarea
                  placeholder="Enter project description"
                  rows="3"
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  required
                ></textarea>
              </div>
            </div>
            <div className="form-group-row dual-fields">
              <div className="form-field">
                <label>Start Date</label>
                <input
                  type="date"
                  value={projectForm.startdate}
                  onChange={(e) => setProjectForm({ ...projectForm, startdate: e.target.value })}
                  required
                />
              </div>
              <div className="form-field">
                <label>End Date</label>
                <input
                  type="date"
                  value={projectForm.enddate}
                  onChange={(e) => setProjectForm({ ...projectForm, enddate: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-actions-centered">
              <button
                type="submit"
                className="crm-btn-primary"
                disabled={isSubmittingProject}
              >
                {isSubmittingProject ? "Creating..." : "Create Project"}
              </button>
            </div>
          </form>
        </div>

        {/* Create Project Module Form */}
        <div className="project-form-card">
          <div className="form-header">
            <h5>Create Project Module</h5>
          </div>
          <form className="crm-form" onSubmit={handleModuleSubmit}>
            <div className="form-group-row">
              <div className="form-field">
                <label>Select Project</label>
                <select
                  value={moduleForm.projectid}
                  onChange={(e) => setModuleForm({ ...moduleForm, projectid: e.target.value })}
                  required
                >
                  <option value="">{loadingProjects ? "Loading..." : "Select a project"}</option>
                  {projects.map(project => (
                    <option key={project.projectid} value={project.projectid}>
                      {project.projectname}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group-row">
              <div className="form-field">
                <label>Module Name</label>
                <input
                  type="text"
                  placeholder="Enter module name"
                  value={moduleForm.modulename}
                  onChange={(e) => setModuleForm({ ...moduleForm, modulename: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-group-row">
              <div className="form-field">
                <label>Description</label>
                <textarea
                  placeholder="Enter module description"
                  rows="3"
                  value={moduleForm.description}
                  onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                  required
                ></textarea>
              </div>
            </div>
            <div className="form-actions-centered">
              <button
                type="submit"
                className="crm-btn-primary"
                disabled={isSubmittingModule}
              >
                {isSubmittingModule ? "Creating..." : "Create Module"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Projects;
